import AddBusinessRounded from '@mui/icons-material/AddBusinessRounded';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { RequireCapability } from '../../auth/RequireAuth';
import { DataTable, SearchField, type Column } from '../../components/DataTable';
import { EmptyState, PageHeader, QueryError } from '../../components/states';
import { Segmented } from '../../components/ui';
import { count, date, plural } from '../../lib/format';
import { daysFromNow, fetchTenants, tenantKeys, type Tenant } from './api';
import { BILLING_OPTIONS, buildTenantQuery, PAID_OPTIONS, STATUS_OPTIONS } from './listQuery';
import { CreateTenantDialog } from './CreateTenantDialog';
import { BillingChip, TenantAvatar, TenantStatusChip } from './parts';

function PaidThrough({ tenant }: { tenant: Tenant }) {
  const s = tenant.subscription;
  if (!s) return <>—</>;
  const d = daysFromNow(s.paidThrough);
  const tone = d < 0 ? 'error.main' : d <= 7 ? 'warning.main' : 'text.secondary';
  return (
    <Box>
      <Box component="span" className="tabular" sx={{ whiteSpace: 'nowrap' }}>
        {date(s.paidThrough)}
      </Box>
      <Typography variant="caption" component="div" sx={{ color: tone }}>
        {d === 0 ? 'Ends today' : d > 0 ? `${plural(d, 'day')} left` : `${plural(-d, 'day')} ago`}
      </Typography>
    </Box>
  );
}

const columns: Column<Tenant>[] = [
  {
    key: 'shop',
    header: 'Shop',
    render: (t) => (
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
        <TenantAvatar name={t.name} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 650 }} noWrap>
            {t.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" noWrap>
            {t.slug}
          </Typography>
        </Box>
      </Stack>
    ),
  },
  { key: 'status', header: 'Status', render: (t) => <TenantStatusChip tenant={t} /> },
  { key: 'billing', header: 'Billing', render: (t) => <BillingChip onTrial={t.onTrial} /> },
  {
    key: 'plan',
    header: 'Plan',
    hideBelow: 'md',
    render: (t) =>
      t.subscription?.plan?.name ?? (
        <Typography variant="body2" color="text.secondary" component="span">
          No plan
        </Typography>
      ),
  },
  {
    key: 'paid',
    header: 'Paid through',
    hideBelow: 'sm',
    render: (t) => <PaidThrough tenant={t} />,
  },
  {
    key: 'created',
    header: 'Created',
    hideBelow: 'lg',
    render: (t) => (
      <Box component="span" className="tabular" sx={{ whiteSpace: 'nowrap' }}>
        {date(t.createdAt)}
      </Box>
    ),
  },
];

function TenantsList() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [creating, setCreating] = useState(false);
  const [params, setParams] = useSearchParams();
  // One "now" per visit keeps the paid-through filter's query key stable.
  const [now] = useState(() => Date.now());
  // Remounts the search box when the filters are cleared (it keeps its own text while typing).
  const [searchKey, setSearchKey] = useState(0);
  const { query, status, paid, billing, q } = useMemo(
    () => buildTenantQuery(params, now),
    [params, now],
  );

  const update = useCallback(
    (changes: Record<string, string | null>, resetPage = true) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(changes)) {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          }
          if (resetPage) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const list = useQuery({
    queryKey: tenantKeys.list(query),
    queryFn: () => fetchTenants(query),
    placeholderData: keepPreviousData,
  });

  const filtered = Boolean(query.q) || status !== 'all' || paid !== 'any' || billing !== 'all';
  const total = list.data?.page.total;

  return (
    <>
      <PageHeader
        eyebrow="Platform"
        title="Tenants"
        actions={
          can('tenants.create') && (
            <Button
              variant="contained"
              startIcon={<AddBusinessRounded />}
              onClick={() => setCreating(true)}
            >
              New tenant
            </Button>
          )
        }
        subtitle={
          total === undefined
            ? 'Every shop on the platform, with its subscription.'
            : filtered
              ? `${plural(total, 'shop')} match these filters.`
              : `${plural(total, 'shop')} on the platform.`
        }
      />
      {list.isError && <QueryError error={list.error} onRetry={() => void list.refetch()} />}
      <DataTable
        columns={columns}
        rows={list.data?.data}
        rowKey={(t) => t.id}
        loading={list.isPending}
        page={list.data?.page}
        onPageChange={(p) => update({ page: p === 1 ? null : String(p) }, false)}
        onPageSizeChange={(s) => update({ size: s === 25 ? null : String(s) })}
        onRowClick={(t) => void navigate(`/tenants/${t.id}`)}
        toolbar={
          <>
            <SearchField
              key={searchKey}
              value={q}
              onChange={(v) => update({ q: v })}
              placeholder="Search by shop name or slug"
            />
            <Box sx={{ flexShrink: 0 }}>
              <Segmented
                label="Status"
                value={status}
                options={STATUS_OPTIONS}
                onChange={(v) => update({ status: v === 'all' ? null : v })}
              />
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              <Segmented
                label="Billing"
                value={billing}
                options={BILLING_OPTIONS}
                onChange={(v) => update({ billing: v === 'all' ? null : v })}
              />
            </Box>
            <TextField
              select
              label="Paid through"
              value={paid}
              onChange={(e) => update({ paid: e.target.value === 'any' ? null : e.target.value })}
              sx={{ width: { sm: 240 }, flexShrink: 0, ml: { sm: 'auto !important' } }}
            >
              {PAID_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </>
        }
        empty={
          filtered ? (
            <EmptyState
              dense
              icon={<StorefrontOutlined />}
              title="No shops match"
              body="Try another search or clear the filters."
              action={{
                label: 'Clear filters',
                onClick: () => {
                  setSearchKey((k) => k + 1);
                  setParams({}, { replace: true });
                },
              }}
            />
          ) : (
            <EmptyState
              dense
              icon={<StorefrontOutlined />}
              title="No shops yet"
              body="Shops appear here as soon as they register."
            />
          )
        }
      />
      <CreateTenantDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(t) => void navigate(`/tenants/${t.id}`)}
      />
      {list.data && list.data.page.total > 0 && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
          Showing {count(list.data.data.length)} of {count(list.data.page.total)}. Newest first.
        </Typography>
      )}
    </>
  );
}

export function TenantsPage() {
  return (
    <RequireCapability anyOf={['tenants.view']} what="tenants">
      <TenantsList />
    </RequireCapability>
  );
}
